// components/CommissionDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  IconButton,
  Alert,
  Skeleton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Receipt,
  Schedule,
  Store,
  ShoppingCart,
  Info,
  ContentCopy,
  SmartToy,
  AccessTime,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';

const CommissionDetail = () => {
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromInvoice = location.state?.fromInvoice;
  const fromInvoiceTestMode = location.state?.testMode;
  // The order list it came from: HandOff's invoice or the retailer's invoice to HandOff.
  const fromInvoicePath = `/${location.state?.fromInvoiceKind === 'self_bill' ? 'self-bills' : 'invoices'}/${fromInvoice}`;

  const loadCommissionDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getCommissionDetail(id);
      if (response.success) {
        setCommission(response.data);
      } else {
        setError('Commission not found');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCommissionDetail();
  }, [loadCommissionDetail]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusColors = {
    'auto_approved': 'success',
    'manually_approved': 'success',
    'pending': 'info',
    'declined': 'error',
    'approved': 'success', // Consolidated status
  };

  const statusLabels = {
    'auto_approved': 'Approved',
    'manually_approved': 'Approved', 
    'pending': 'Pending',
    'declined': 'Declined',
    'approved': 'Approved', // Consolidated status
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="200px" height={40} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => fromInvoice
          ? navigate(`${fromInvoicePath}${fromInvoiceTestMode ? '?test_mode=true' : ''}`)
          : navigate('/commissions', { state: location.state })}>
          {fromInvoice ? 'Back to Invoice' : 'Back to Commissions'}
        </Button>
      </Box>
    );
  }

  const isOwed = commission.commission_type === 'owed';
  const commissionAmount = isOwed
    ? commission.match_retailer_commission_paid
    : commission.reference_retailer_commission_amount;
  // Receiving retailer pays the full rate (10%); sending retailer earns it minus HandOff's
  // share (8%). One rate row, in the retailer's own terms; "bid" and "20%" are internal.
  const bid = Number(commission.match_retailer_bid || 0);
  const rawRate = isOwed ? bid : bid * (1 - Number(commission.handoff_commission_rate || 0) / 100);
  const commissionRate = Number.isInteger(rawRate) ? String(rawRate) : rawRate.toFixed(1);

  const handleBack = () => fromInvoice
    ? navigate(`${fromInvoicePath}${fromInvoiceTestMode ? '?test_mode=true' : ''}`)
    : navigate('/commissions', { state: location.state });

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1">
            Commission Detail
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Handoff Commission ID: {commission.id}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Commission Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commission Overview
              </Typography>
              <Box mb={2} display="flex" flexWrap="wrap" gap={1}>
                <Chip
                  label={isOwed ? 'I Owe' : 'I Earn'}
                  color={isOwed ? 'error' : 'success'}
                  size="large"
                />
                <Chip
                  label={statusLabels[commission.status] || commission.status}
                  color={statusColors[commission.status] || 'default'}
                  size="large"
                />
                {/* Cookie Already Used Badge (for declined items) */}
                {commission.duplicate_indicators?.is_duplicate_cookie && (
                  <Chip
                    icon={<ContentCopy />}
                    label="Cookie Already Used"
                    color="warning"
                    size="large"
                  />
                )}
                {/* Duplicate Order Item Badge */}
                {commission.duplicate_indicators?.is_duplicate_order_item && (
                  <Chip
                    icon={<ContentCopy />}
                    label="Duplicate Order Item"
                    color="warning"
                    size="large"
                  />
                )}
                {/* Expired Cookie Badge */}
                {commission.cookie_expired && (
                  <Chip
                    icon={<AccessTime />}
                    label="Cookie Expired"
                    color="warning"
                    size="large"
                  />
                )}
                {/* Cookie Already Used Badge (for pending items) */}
                {commission.status === 'pending' && commission.cookie_already_used?.is_used && (
                  <Chip
                    icon={<ContentCopy />}
                    label="Cookie Already Used"
                    color="error"
                    size="large"
                  />
                )}
              </Box>
              <Typography variant="h4" color={isOwed ? 'error.main' : 'success.main'} gutterBottom>
                {formatCurrency(commissionAmount)}
              </Typography>
              
              {/* Cookie/Order Item Already Used Alert for Declined Commissions */}
              {commission.status === 'declined' && commission.duplicate_indicators && (
                commission.duplicate_indicators.is_duplicate_cookie ||
                commission.duplicate_indicators.is_duplicate_order_item
              ) && (
                <Alert
                  severity="warning"
                  icon={<ContentCopy />}
                  sx={{ my: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {commission.duplicate_indicators.is_duplicate_cookie
                      ? 'Cookie Already Used'
                      : 'Another Cookie Was Approved'}
                  </Typography>
                  <Typography variant="body2">
                    {(() => {
                      const conflictOrder = commission.duplicate_indicators.conflicting_order_number;
                      const isSameOrder = conflictOrder && conflictOrder === commission.order_number;

                      if (commission.duplicate_indicators.is_duplicate_cookie) {
                        if (isSameOrder) {
                          return 'Another item on this order was already approved with this cookie.';
                        }
                        return `This cookie was already used to approve a commission on order #${conflictOrder}.`;
                      } else {
                        // is_duplicate_order_item
                        return 'This order item already has an approved commission from a different click.';
                      }
                    })()}
                  </Typography>
                </Alert>
              )}

              {/* Cookie Already Used Alert (for pending items) */}
              {commission.status === 'pending' && commission.cookie_already_used?.is_used && (
                <Alert
                  severity="error"
                  icon={<ContentCopy />}
                  sx={{ my: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Cookie Already Used
                  </Typography>
                  <Typography variant="body2">
                    {(() => {
                      const conflictOrder = commission.cookie_already_used.conflicting_order_number;
                      const isSameOrder = conflictOrder && conflictOrder === commission.order_number;

                      if (isSameOrder) {
                        return 'Another item on this order was already approved with this cookie. This commission will be automatically declined.';
                      }
                      return `This cookie was already used to approve a commission on order #${conflictOrder}. This commission will be automatically declined.`;
                    })()}
                  </Typography>
                </Alert>
              )}

              {/* Expired Cookie Alert */}
              {commission.cookie_expired && (
                <Alert
                  severity="warning"
                  icon={<AccessTime />}
                  sx={{ my: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Cookie Expired at Time of Order
                  </Typography>
                  <Typography variant="body2">
                    The tracking cookie was {Math.round(commission.conversion_time_hours / 24)} days old
                    when this order was placed, exceeding the 30-day attribution window.
                  </Typography>
                </Alert>
              )}

              {/* Standard Decline Reason Alert (for non-duplicate declines) */}
              {commission.status === 'declined' && commission.status_reason &&
               !(commission.duplicate_indicators?.is_duplicate_cookie ||
                 commission.duplicate_indicators?.is_duplicate_order_item) && (
                <Alert
                  severity="warning"
                  icon={<Info />}
                  sx={{ my: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Decline Reason:
                  </Typography>
                  <Typography variant="body2">
                    {commission.status_reason}
                  </Typography>
                </Alert>
              )}

              {/* AI Recommendation (available for non-perfect matches) */}
              {commission.ai_recommendation && (
                <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <SmartToy sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="primary.main">
                      AI Recommendation
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      label={`${commission.ai_recommendation.recommendation.toUpperCase()} (${Math.round(commission.ai_recommendation.confidence * 100)}%)`}
                      color={commission.ai_recommendation.recommendation === 'approve' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  {commission.ai_recommendation.reasoning && (
                    <Typography variant="body2" color="text.secondary">
                      {commission.ai_recommendation.reasoning}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Item Price:
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(commission.item_price)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Commission Rate:
                </Typography>
                <Typography variant="body2">
                  {commissionRate}% of item price
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Match Quality:
                </Typography>
                <Typography variant="body2">
                  {commission.cookie_match_level.replace('_', ' ')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Receipt sx={{ verticalAlign: 'middle', mr: 1 }} />
                Order Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Order Number
                </Typography>
                <Typography variant="h6">
                  {commission.order_number}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Order Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(commission.order_timestamp)}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Order Total
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(commission.order_total)} {commission.order_currency}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Retailer
                </Typography>
                <Typography variant="body1">
                  {commission.order_retailer}
                </Typography>
              </Box>
              {commission.conversion_time_hours && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Conversion Time
                  </Typography>
                  <Typography variant="body1">
                    {Math.round(commission.conversion_time_hours * 10) / 10} hours
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Product Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                Product Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Product Name
                </Typography>
                <Typography variant="body1">
                  {commission.product_name}
                </Typography>
              </Box>
              {commission.product_color && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Color
                  </Typography>
                  <Typography variant="body1">
                    {commission.product_color}
                  </Typography>
                </Box>
              )}
              {commission.product_size && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Size
                  </Typography>
                  <Typography variant="body1">
                    {commission.product_size}
                  </Typography>
                </Box>
              )}
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Quantity
                </Typography>
                <Typography variant="body1">
                  {commission.quantity}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Unit Price
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(commission.item_price)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Partner Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Store sx={{ verticalAlign: 'middle', mr: 1 }} />
                Trading Partners
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Buying Retailer {commission.buying_retailer_id === commission.retailer_id ? '(You)' : ''}
                </Typography>
                <Typography variant="body1">
                  {commission.buying_retailer_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Selling Retailer {commission.selling_retailer_id === commission.retailer_id ? '(You)' : ''}
                </Typography>
                <Typography variant="body1">
                  {commission.selling_retailer_name}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tracking Information */}
        {commission.click_timestamp && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Schedule sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Tracking Timeline
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Referral Click</TableCell>
                        <TableCell>{formatDate(commission.click_timestamp)}</TableCell>
                        <TableCell>
                          {[
                            commission.match_brand,
                            commission.match_article_number,
                            commission.match_article_name,
                            commission.match_article_color,
                            commission.match_article_size
                          ].filter(Boolean).join(' - ')}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Order Placed</TableCell>
                        <TableCell>{formatDate(commission.order_timestamp)}</TableCell>
                        <TableCell>Order #{commission.order_number}</TableCell>
                      </TableRow>
                      {commission.invoiced_at && (
                        <TableRow>
                          <TableCell>Invoiced</TableCell>
                          <TableCell>{formatDate(commission.invoiced_at)}</TableCell>
                          <TableCell>
                            {commission.invoice_number ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => navigate(`/invoices/${commission.invoice_id}`)}
                                >
                                  {commission.invoice_number}
                                </Link>
                                {commission.invoice_settlement_status && (
                                  <Chip
                                    label={
                                      commission.invoice_settlement_status === 'issued' ? 'Open'
                                        : commission.invoice_settlement_status.charAt(0).toUpperCase() + commission.invoice_settlement_status.slice(1)
                                    }
                                    color={
                                      commission.invoice_settlement_status === 'settled' ? 'success' :
                                      commission.invoice_settlement_status === 'disputed' ? 'error' :
                                      commission.invoice_settlement_status === 'issued' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                )}
                              </Box>
                            ) : (
                              `Invoice ID: ${commission.invoice_id}`
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              onClick={handleBack}
            >
              {fromInvoice ? 'Back to Invoice' : 'Back to Commissions'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommissionDetail;